import { useEffect, useMemo, useRef } from 'react';

import { cn } from '../../../lib/cn';
import { formatHebrewRelative } from '../../../lib/relativeTime';
import { useBotMessages, useBotTransitionLog } from '../botQueries';
import type { BotMessageRow, BotStateTransitionRow } from '../bot.schema';
import { displayState } from '../bot.schema';
import styles from './ConversationTimeline.module.css';

interface ConversationTimelineProps {
  orgId: string | null;
  conversationId: string | null;
}

type MergedItem =
  | { kind: 'msg'; row: BotMessageRow; created_at: string }
  | { kind: 'transition'; row: BotStateTransitionRow; created_at: string };

function authorLabel(author: BotMessageRow['author']): string {
  if (author === 'user') return 'משתמש';
  if (author === 'bot') return 'בוט';
  if (author === 'yoav') return 'יואב';
  if (author === 'system') return 'מערכת';
  return '';
}

function outcomeLabel(outcome: BotStateTransitionRow['outcome']): string {
  if (outcome === 'reply') return 'תשובה';
  if (outcome === 'silent') return 'שתיקה';
  if (outcome === 'silent_human_lock') return 'נעילה ידנית';
  if (outcome === 'silent_fallback_cooldown') return 'המתנה בין ניסיונות';
  if (outcome === 'error') return 'שגיאה';
  return '';
}

export function ConversationTimeline({ orgId, conversationId }: ConversationTimelineProps) {
  const messagesQ = useBotMessages(orgId, conversationId);
  const transitionsQ = useBotTransitionLog(orgId, conversationId);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const items = useMemo<MergedItem[]>(() => {
    const msgs: MergedItem[] = (messagesQ.data ?? []).map((row) => ({
      kind: 'msg',
      row,
      created_at: row.created_at,
    }));
    const trans: MergedItem[] = (transitionsQ.data ?? []).map((row) => ({
      kind: 'transition',
      row,
      created_at: row.created_at,
    }));
    return [...msgs, ...trans].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
  }, [messagesQ.data, transitionsQ.data]);

  const loading = messagesQ.isLoading || transitionsQ.isLoading;

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [items.length]);

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>תמלול שיחה</span>
        <span>
          <span className="num">{String(messagesQ.data?.length ?? 0)}</span> הודעות
        </span>
      </div>

      {loading ? (
        <div className={styles.skeleton} aria-label="טוען תמלול">
          <div className={styles.skelLine} />
          <div className={styles.skelLine} />
          <div className={styles.skelLine} />
        </div>
      ) : items.length === 0 ? (
        <p className={styles.empty}>אין הודעות עדיין.</p>
      ) : (
        <div ref={scrollerRef} className={styles.scroller}>
          {items.map((item) =>
            item.kind === 'msg' ? (
              <MessageBubble key={`m-${item.row.id}`} msg={item.row} />
            ) : (
              <TransitionRow key={`t-${item.row.id}`} t={item.row} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg }: { msg: BotMessageRow }) {
  const bubbleClass =
    msg.direction === 'inbound'
      ? styles.bubbleUser
      : msg.author === 'yoav'
        ? styles.bubbleYoav
        : msg.direction === 'system'
          ? styles.bubbleUser
          : styles.bubbleBot;

  return (
    <div className={cn(styles.bubble, bubbleClass)}>
      <div>{msg.body}</div>
      <div className={styles.meta}>
        {msg.author ? <span>{authorLabel(msg.author)}</span> : null}
        <span>·</span>
        <span>{formatHebrewRelative(msg.created_at)}</span>
        {msg.state_at_time ? (
          <span className={styles.metaPill} dir="ltr">
            {msg.state_at_time}
          </span>
        ) : null}
        {msg.is_nudge ? <span className={styles.metaPill}>תזכורת</span> : null}
        {msg.is_fallback ? <span className={styles.metaPill}>fallback</span> : null}
      </div>
    </div>
  );
}

function TransitionRow({ t }: { t: BotStateTransitionRow }) {
  const isError = t.outcome === 'error';
  const isSilent = t.outcome && t.outcome !== 'reply';
  const before = t.state_before ? displayState(t.state_before as never) : '∅';
  const after = t.state_after ? displayState(t.state_after as never) : '∅';
  return (
    <div className={styles.systemRow}>
      <span className={styles.systemLine} />
      <span
        className={cn(
          styles.systemLabel,
          isError && styles.systemBadgeError,
          !isError && isSilent && styles.systemBadgeSilent,
        )}
      >
        <span dir="ltr">{t.transition_id}</span>
        {' · '}
        {before} → {after}
        {t.outcome ? ` · ${outcomeLabel(t.outcome)}` : ''}
      </span>
      <span className={styles.systemLine} />
    </div>
  );
}
